import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { AnnounceContext } from "./context.js";

interface Pairing {
  pairID: string;
  code: string;
  expiresAt: number | string;
}

function parseExpiry(value: number | string): number {
  if (typeof value === "number") return value < 1e12 ? value * 1000 : value;
  const t = Date.parse(value);
  if (!isNaN(t)) return t;
  const n = Number(value);
  return isNaN(n) ? Date.now() + 300000 : n < 1e12 ? n * 1000 : n;
}

const pad = (n: number) => (n < 10 ? "0" : "") + n;

/** DESIGN §16.1: a code to approve from the phone; the page opens by itself once it is. */
export function SignIn({ onApproved }: { onApproved: () => void }) {
  const announce = useContext(AnnounceContext);
  const [code, setCode] = useState<string[] | null>(null);
  const [left, setLeft] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [lifetime, setLifetime] = useState(300);
  const [copied, setCopied] = useState(false);
  const canCopy = typeof navigator !== "undefined" && !!navigator.clipboard?.writeText;
  const approved = useRef(onApproved);
  approved.current = onApproved;

  const newCode = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    document.title = "Sign in · Training Coach Portal";
  }, []);

  useEffect(() => {
    let live = true;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const later = (fn: () => void, ms: number) => timers.push(setTimeout(() => live && fn(), ms));
    setCode(null);
    setLeft(null);

    const poll = (pairID: string) => {
      api<{ status?: string }>("/web/pair/" + encodeURIComponent(pairID)).then(
        (body) => {
          if (!live) return;
          if (body?.status === "approved") {
            live = false;
            approved.current();
            return;
          }
          if (body?.status === "expired") return newCode();
          later(() => poll(pairID), 2000);
        },
        (err: unknown) => {
          if (!live) return;
          if (err instanceof ApiError && (err.status === 410 || err.status === 404)) return newCode();
          later(() => poll(pairID), 4000);
        },
      );
    };

    api<Pairing>("/web/pair", { method: "POST" }).then(
      (body) => {
        if (!live) return;
        if (!body?.pairID) {
          setError("Couldn't get a code. Trying again in 10 seconds.");
          later(newCode, 10000);
          return;
        }
        setError("");
        const chars = String(body.code || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase().split("");
        setCode(chars);
        announce("Your code is " + chars.join(" "));
        const expiresAt = parseExpiry(body.expiresAt);
        setLifetime(Math.max(1, Math.round((expiresAt - Date.now()) / 1000)));
        setCopied(false);
        const tick = () => {
          const seconds = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
          setLeft(seconds);
          if (seconds <= 0) {
            announce("That code expired. Here is a new one.");
            newCode();
            return;
          }
          later(tick, 1000);
        };
        tick();
        later(() => poll(body.pairID), 2000);
      },
      (err: unknown) => {
        if (!live) return;
        if (err instanceof ApiError) {
          const wait = err.status === 429 ? 60000 : 10000;
          setError((err.message || "Couldn't get a code.") + " Trying again in " + wait / 1000 + " seconds.");
          later(newCode, wait);
        } else {
          setError("Couldn't reach the server. Trying again in 10 seconds.");
          later(newCode, 10000);
        }
      },
    );

    return () => {
      live = false;
      timers.forEach(clearTimeout);
    };
  }, [attempt, announce, newCode]);

  const copy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code.join("")).then(
      () => {
        setCopied(true);
        announce("Code copied");
        setTimeout(() => setCopied(false), 2500);
      },
      () => setCopied(false),
    );
  };

  return (
    <div className="signin">
      <main className="signin-card" aria-labelledby="signin-title">
        <img className="signin-mark" src="/apple-touch-icon.png" alt="" width={56} height={56} />
        <h1 id="signin-title">Sign in to the coach portal</h1>
        <p className="signin-lead">Approve this browser from the Training app on your iPhone.</p>
        <div className="code">
          {code ? (
            <>
              <span aria-hidden="true" className="code-row">
                {[code.slice(0, 3), code.slice(3)].map((group, g) => (
                  <span key={g} className="code-pair">
                    {g > 0 ? <span className="code-dash">–</span> : null}
                    <span className="code-group">
                      {group.map((c, i) => (
                        <span key={i} className="code-char">
                          {c}
                        </span>
                      ))}
                    </span>
                  </span>
                ))}
              </span>
              <span className="sr">Code: {code.join(" ")}</span>
            </>
          ) : error ? null : (
            <span className="code-wait">Getting a code…</span>
          )}
        </div>
        {code ? (
          <div className="signin-code-tools">
            {canCopy ? (
              <button type="button" className="btn signin-copy" onClick={copy}>
                {copied ? (
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="m4.5 10.5 3.5 3.5 7.5-8" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" d="M7 7V4.5A1.5 1.5 0 0 1 8.5 3h7A1.5 1.5 0 0 1 17 4.5v7a1.5 1.5 0 0 1-1.5 1.5H13M4.5 7h7A1.5 1.5 0 0 1 13 8.5v7a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 15.5v-7A1.5 1.5 0 0 1 4.5 7Z" />
                  </svg>
                )}
                {copied ? "Copied" : "Copy code"}
              </button>
            ) : null}
            {left != null ? (
              <div className="signin-expiry">
                <span className="signin-bar" aria-hidden="true">
                  <span style={{ width: `${Math.min(100, (left / lifetime) * 100)}%` }} />
                </span>
                <span className="countdown num">{"Expires in " + Math.floor(left / 60) + ":" + pad(left % 60)}</span>
              </div>
            ) : null}
          </div>
        ) : null}
        <ol className="signin-steps">
          <li>
            On your iPhone, open Training: <b>Settings → Sign in on the web</b>.
          </li>
          <li>{canCopy ? "Type the code, or paste it: copied here, it's on your iPhone too." : "Type the code."}</li>
          <li>
            Tap <b>Approve</b>. This page opens by itself.
          </li>
        </ol>
        {error ? (
          <p className="signin-error" role="alert">
            {error}
          </p>
        ) : code ? (
          <p className="signin-waiting" role="status">
            <span className="signin-dot" aria-hidden="true" />
            Waiting for your phone…
          </p>
        ) : null}
        <button type="button" className="btn signin-new" onClick={newCode}>
          New code
        </button>
        <p className="signin-foot">For coaches and the team&rsquo;s admin.</p>
      </main>
    </div>
  );
}
